import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Tooltip, Alert, Tag } from 'antd';
import {
  ShoppingCartOutlined, DollarOutlined, ClockCircleOutlined,
  TeamOutlined, ReloadOutlined, ArrowUpOutlined, RightOutlined,
} from '@ant-design/icons';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useClient } from '../../context/ClientContext';
import { resolveImgUrl } from '../../templates/4/utils';
import { hexAlpha } from '../../utils/colorUtils';

const { Text } = Typography;

const fmt = (n, currency = false) => {
  if (n == null) return '—';
  if (currency) return `$${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  return Number(n).toLocaleString('es-AR');
};

const DAYS_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

// ── Skeleton pulse ───────────────────────────────────────────────────────────
const Sk = ({ w = '100%', h = 16, r = 8, style = {} }) => (
  <div style={{
    width: w, height: h, borderRadius: r,
    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
    backgroundSize: '200% 100%',
    animation: 'skPulse 1.4s ease-in-out infinite',
    flexShrink: 0,
    ...style,
  }} />
);

// ── Full skeleton layout ─────────────────────────────────────────────────────
const DashboardSkeleton = () => (
  <>
    <style>{`
      @keyframes skPulse {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `}</style>
    {/* Stat cards */}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 20 }}>
      {[0,1,2,3].map(i => (
        <div key={i} style={{ background: '#fff', borderRadius: 14, padding: '20px 24px', display: 'flex', gap: 16, alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <Sk w={48} h={48} r={12} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Sk w="60%" h={12} />
            <Sk w="80%" h={24} />
            <Sk w="50%" h={10} />
          </div>
        </div>
      ))}
    </div>
    {/* Middle row */}
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 14, marginBottom: 20 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <Sk w="40%" h={16} style={{ marginBottom: 8 }} />
        <Sk w="60%" h={12} style={{ marginBottom: 24 }} />
        <Sk w="100%" h={140} r={6} />
      </div>
      <div style={{ background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Sk w="60%" h={16} />
        {[0,1,2,3].map(i => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Sk w="70%" h={12} />
            <Sk w="100%" h={6} r={4} />
          </div>
        ))}
      </div>
    </div>
    {/* Bottom row */}
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
      {[0,1].map(col => (
        <div key={col} style={{ background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Sk w="50%" h={16} />
          {[0,1,2,3,4].map(i => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Sk w={34} h={34} r={8} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Sk w="70%" h={12} />
                <Sk w="40%" h={10} />
              </div>
              <Sk w={60} h={14} r={6} />
            </div>
          ))}
        </div>
      ))}
    </div>
  </>
);

// ── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, sub, color, trend }) => (
  <div style={{
    background: '#fff', borderRadius: 14, padding: '20px 24px',
    display: 'flex', alignItems: 'center', gap: 16,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  }}>
    <div style={{
      width: 48, height: 48, borderRadius: 12, flexShrink: 0,
      background: hexAlpha(color, 0.12),
      display: 'flex', alignItems: 'center', justifyContent: 'center', color,
    }}>
      {icon}
    </div>
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>{value}</div>
      {sub && (
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
          {trend === 'up' && <ArrowUpOutlined style={{ color: '#10b981', fontSize: 11 }} />}
          {sub}
        </div>
      )}
    </div>
  </div>
);

// ── Bar chart ────────────────────────────────────────────────────────────────
const BAR_AREA_HEIGHT = 140;

const BarChart = ({ data, color }) => {
  if (!data || !data.length) return (
    <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Text type="secondary" style={{ fontSize: 13 }}>Sin datos para este período</Text>
    </div>
  );
  const max = Math.max(...data.map(d => Number(d.total) || 0), 1);
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: BAR_AREA_HEIGHT, flexShrink: 0 }}>
        {[max, max * 0.5, 0].map((v, i) => (
          <span key={i} style={{ fontSize: 10, color: '#d1d5db' }}>{fmt(v, true)}</span>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, flex: 1 }}>
        {data.map((d, i) => {
          const total = Number(d.total) || 0;
          const ratio = max > 0 ? total / max : 0;
          const barHeight = Math.max(ratio * BAR_AREA_HEIGHT, 2);
          const date = new Date(d.fecha + 'T00:00:00');
          const label = DAYS_ES[date.getDay()];
          return (
            <Tooltip key={i} title={`${d.fecha}: ${fmt(total, true)} (${d.count} pedidos)`}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'default' }}>
                <div style={{ height: BAR_AREA_HEIGHT, width: '100%', display: 'flex', alignItems: 'flex-end' }}>
                  <div style={{
                    width: '100%', borderRadius: '4px 4px 0 0',
                    height: barHeight,
                    background: hexAlpha(color, 0.85),
                    transition: 'height 0.4s ease',
                  }} />
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{label}</div>
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

// ── Estado badges con barras ─────────────────────────────────────────────────
const EstadoBadges = ({ data }) => {
  if (!data || !data.length) return (
    <Text type="secondary" style={{ fontSize: 13 }}>Sin datos</Text>
  );
  const total = data.reduce((a, b) => a + (b.count || 0), 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {data.slice(0, 5).map((e, i) => {
        const pct = total > 0 ? Math.round((e.count / total) * 100) : 0;
        return (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={{ fontSize: 13, color: '#374151' }}>{e.nombre || 'Sin estado'}</span>
              <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>{pct}%</span>
            </div>
            <div style={{ height: 6, borderRadius: 4, background: '#f3f4f6', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, borderRadius: 4, background: e.color || '#6b7280', transition: 'width 0.6s ease' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Avatar ───────────────────────────────────────────────────────────────────
const Avatar = ({ src, name, color, size = 36 }) => {
  const [err, setErr] = useState(false);
  const initials = (name || '?').slice(0, 2).toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: 8, flexShrink: 0,
      background: hexAlpha(color, 0.12),
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color, fontWeight: 700, fontSize: size * 0.35, overflow: 'hidden',
    }}>
      {src && !err
        ? <img src={resolveImgUrl(src)} alt={name} style={{ width: '80%', height: '80%', objectFit: 'contain' }} onError={() => setErr(true)} />
        : initials}
    </div>
  );
};

// ── Main Dashboard ───────────────────────────────────────────────────────────
export const Dashboard = () => {
  const { tienda, user } = useAuth();
  const { theme } = useClient();
  const navigate = useNavigate();
  const primary = theme?.primaryColor || '#4f46e5';
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const cargar = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true); setError(null);
    try {
      const res = await api.admin.getDashboard(ctrl.signal);
      if (ctrl.signal.aborted) return;
      if (res.status === 'success') setStats(res.data);
      else setError(res.message || 'No se pudo cargar el dashboard.');
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setError('Error al conectar con el servidor.');
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [cargar]);

  const card = {
    background: '#fff',
    borderRadius: 14,
    padding: 20,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  };

  return (
    <>
      <style>{`
        @keyframes skPulse {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .dash-middle { display: grid; grid-template-columns: 1fr 300px; gap: 14px; margin-bottom: 20px; }
        .dash-bottom  { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        @media (max-width: 900px) {
          .dash-middle { grid-template-columns: 1fr !important; }
          .dash-bottom  { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, gap: 12, flexWrap: 'wrap' }}>
          <div>
            <Text strong style={{ fontSize: 26, lineHeight: 1.2, display: 'block', color: '#111827' }}>
              Dashboard
            </Text>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {tienda?.display_name || tienda?.nombre} — Panel de administración
            </Text>
          </div>
          <Tooltip title="Actualizar">
            <button
              onClick={cargar}
              disabled={loading}
              style={{
                width: 36, height: 36, border: '1px solid #e5e7eb', borderRadius: 8,
                background: '#fff', cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#6b7280', opacity: loading ? 0.5 : 1,
              }}
            >
              <ReloadOutlined spin={loading} />
            </button>
          </Tooltip>
        </div>

        {error && <Alert type="error" message={error} style={{ marginBottom: 16, borderRadius: 10 }} />}

        {/* ── SKELETON ── */}
        {loading && <DashboardSkeleton />}

        {/* ── CONTENIDO ── */}
        {!loading && stats && (
          <>
            {/* Stat Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 20 }}>
              <StatCard
                icon={<ShoppingCartOutlined style={{ fontSize: 22 }} />}
                label="Pedidos completados"
                value={fmt(stats.total_pedidos)}
                sub={`${fmt(stats.pedidos_este_mes)} este mes`}
                color={primary}
              />
              <StatCard
                icon={<DollarOutlined style={{ fontSize: 22 }} />}
                label="Revenue total"
                value={fmt(stats.ingreso_total, true)}
                sub={`${fmt(stats.ingreso_este_mes, true)} este mes`}
                color="#10b981"
                trend="up"
              />
              <StatCard
                icon={<ClockCircleOutlined style={{ fontSize: 22 }} />}
                label="Cobros pendientes"
                value={fmt(stats.cobros_pendientes)}
                sub={stats.cobros_pendientes > 0 ? 'Requieren atención' : 'Todo al día'}
                color={stats.cobros_pendientes > 0 ? '#f59e0b' : '#10b981'}
              />
              <StatCard
                icon={<TeamOutlined style={{ fontSize: 22 }} />}
                label="Clientes"
                value={fmt(stats.total_clientes)}
                color="#8b5cf6"
              />
            </div>

            {/* Middle Row: Chart + Estados */}
            <div className="dash-middle">
              <div style={card}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong style={{ fontSize: 16, color: '#111827' }}>Ventas esta semana</Text>
                  <Text type="secondary" style={{ fontSize: 13, display: 'block' }}>Ingresos por día (últimos 7 días)</Text>
                </div>
                <BarChart data={stats.ventas_semana} color={primary} />
              </div>
              <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                  <div>
                    <Text strong style={{ fontSize: 16, color: '#111827' }}>Estado de pedidos</Text>
                    <Text type="secondary" style={{ fontSize: 13, display: 'block' }}>Distribución por estado</Text>
                  </div>
                </div>
                <EstadoBadges data={stats.pedidos_por_estado} />
                <button
                  onClick={() => navigate('pedidos')}
                  style={{
                    marginTop: 16, display: 'flex', alignItems: 'center', gap: 4,
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: primary, fontSize: 13, fontWeight: 600, padding: 0,
                  }}
                >
                  Ver todos los pedidos <RightOutlined style={{ fontSize: 11 }} />
                </button>
              </div>
            </div>

            {/* Bottom Row: Top Productos + Últimos pedidos */}
            <div className="dash-bottom">
              {/* Top Productos */}
              <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div>
                    <Text strong style={{ fontSize: 16, color: '#111827' }}>Top Productos</Text>
                    <Text type="secondary" style={{ fontSize: 13, display: 'block' }}>Más vendidos (últimos 30 días)</Text>
                  </div>
                  <button
                    onClick={() => navigate('productos')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: primary, fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, padding: 0 }}
                  >
                    Ver todos <RightOutlined style={{ fontSize: 11 }} />
                  </button>
                </div>

                {!stats.top_productos?.length ? (
                  <Text type="secondary" style={{ fontSize: 13 }}>Sin ventas en los últimos 30 días.</Text>
                ) : (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, padding: '0 0 8px', borderBottom: '1px solid #f3f4f6', marginBottom: 4 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Producto</Text>
                      <Text type="secondary" style={{ fontSize: 12, textAlign: 'right' }}>Cant.</Text>
                      <Text type="secondary" style={{ fontSize: 12, textAlign: 'right', minWidth: 80 }}>Total</Text>
                    </div>
                    {stats.top_productos.map((p, i) => (
                      <div
                        key={p.id_producto}
                        style={{
                          display: 'grid', gridTemplateColumns: '1fr auto auto',
                          gap: 8, padding: '10px 0', alignItems: 'center',
                          borderBottom: i < stats.top_productos.length - 1 ? '1px solid #f9fafb' : 'none',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                          <Avatar src={p.imagen} name={p.nombre} color={primary} size={34} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.nombre}</div>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>{p.codigo || '—'}</div>
                          </div>
                        </div>
                        <div style={{ fontSize: 13, color: '#374151', textAlign: 'right', fontWeight: 500 }}>×{p.cantidad_vendida}</div>
                        <div style={{ fontSize: 13, color: '#10b981', textAlign: 'right', fontWeight: 600, minWidth: 80 }}>{fmt(p.total_generado, true)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Últimos Pedidos */}
              <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div>
                    <Text strong style={{ fontSize: 16, color: '#111827' }}>Últimos pedidos</Text>
                    <Text type="secondary" style={{ fontSize: 13, display: 'block' }}>Actividad reciente</Text>
                  </div>
                  <button
                    onClick={() => navigate('pedidos')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: primary, fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, padding: 0 }}
                  >
                    Ver todos <RightOutlined style={{ fontSize: 11 }} />
                  </button>
                </div>

                {!stats.ultimos_pedidos?.length ? (
                  <Text type="secondary" style={{ fontSize: 13 }}>No hay pedidos recientes.</Text>
                ) : (
                  <div>
                    {stats.ultimos_pedidos.map((p, i) => (
                      <div
                        key={p.id}
                        onClick={() => navigate(`pedidos/${p.id}`)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
                          borderBottom: i < stats.ultimos_pedidos.length - 1 ? '1px solid #f9fafb' : 'none',
                          cursor: 'pointer', transition: 'opacity 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.opacity = '0.65'}
                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                      >
                        <div style={{
                          width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                          background: hexAlpha(primary, 0.10),
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: primary, fontWeight: 700, fontSize: 12,
                        }}>
                          #{p.id}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.cliente}</div>
                          <div style={{ fontSize: 12, color: '#9ca3af' }}>{p.fecha}</div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{fmt(p.total, true)}</div>
                          <Tag style={{
                            fontSize: 11, borderRadius: 6, margin: '2px 0 0', padding: '0 6px', border: 'none',
                            background: hexAlpha(p.estado_pago_color || '#6b7280', 0.12),
                            color: p.estado_pago_color || '#6b7280',
                          }}>
                            {p.estado_pago || p.estado_nombre || '—'}
                          </Tag>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {stats.cobros_pendientes > 0 && (
                  <button
                    onClick={() => navigate('cobros')}
                    style={{
                      marginTop: 14, width: '100%', padding: '10px 14px',
                      background: hexAlpha('#f59e0b', 0.08), border: `1px solid ${hexAlpha('#f59e0b', 0.3)}`,
                      borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      color: '#b45309', fontSize: 13, fontWeight: 600,
                    }}
                  >
                    <span>⚠️ {stats.cobros_pendientes} cobro{stats.cobros_pendientes !== 1 ? 's' : ''} por revisar</span>
                    <RightOutlined style={{ fontSize: 11 }} />
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};
